import React, { useMemo, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';

type SmartImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'style'> & {
    containerWidth?: number;
    containerHeight?: number;
    containerSx?: SxProps<Theme>;
    imageStyle?: React.CSSProperties;
    blurredBackground?: boolean;
    fallbackBackground?: string;
};

const SmartImageV2: React.FC<SmartImageProps> = ({
    src,
    alt = '',
    containerWidth = 300,
    containerHeight = 200,
    containerSx,
    imageStyle,
    blurredBackground = true,
    fallbackBackground = '#f3f3f3',
    ...imgProps
}) => {
    const [ fitBy, setFitBy ] = useState<'width' | 'height' | null>(null);

    const containerRatio = useMemo(() => {
        return containerWidth / containerHeight;
    }, [ containerWidth, containerHeight ]);

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        const imageRatio = img.naturalWidth / img.naturalHeight;

        if (imageRatio >= containerRatio) {
            setFitBy('width');
        } else {
            setFitBy('height');
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: containerWidth,
                height: containerHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                backgroundColor: fallbackBackground,
                boxSizing: 'border-box',
                ...containerSx,
            }}
        >
            {blurredBackground && src ? (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url("${src}")`,
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover',
                        filter: 'blur(20px)',
                        transform: 'scale(1.08)',
                        opacity: 0.25,
                        pointerEvents: 'none',
                    }}
                />
            ) : null}

            <Box
                component="img"
                src={src}
                alt={alt}
                onLoad={handleLoad}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'block',
                    ...(
                        fitBy === 'width'
                            ? {
                                width: '100%',
                                height: 'auto',
                                maxHeight: '100%',
                            }
                            : fitBy === 'height'
                                ? {
                                    height: '100%',
                                    width: 'auto',
                                    maxWidth: '100%',
                                }
                                : {
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    width: 'auto',
                                    height: 'auto',
                                }
                    ),
                }}
                style={imageStyle}
                {...imgProps}
            />
        </Box>
    );
};

export default SmartImageV2;
